import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Observable } from 'rxjs';
import { DnpeImportResult } from '@k12/models';

export interface DnpeImportDialogData {
  importQuery: (file: File) => Observable<DnpeImportResult>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-dnpe-import-dialog',
  templateUrl: './dnpe-import-dialog.component.html',
  styleUrl: './dnpe-import-dialog.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule, MatProgressBarModule]
})
export class DnpeImportDialogComponent {
  phase: 'idle' | 'processing' | 'done' = 'idle';
  result: DnpeImportResult | null = null;
  importError: string | null = null;
  xlsxError: string | null = null;

  readonly dialogRef = inject(MatDialogRef<DnpeImportDialogComponent>);
  data = inject<DnpeImportDialogData>(MAT_DIALOG_DATA);
  private readonly cdr = inject(ChangeDetectorRef);

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.xlsxError = 'Only .xlsx files are accepted. Please select the .xlsx file from the DNPE weekly update email.';
      (event.target as HTMLInputElement).value = '';
      return;
    }

    this.xlsxError = null;
    this.phase = 'processing';
    this.importError = null;
    this.result = null;

    this.data.importQuery(file).subscribe({
      next: (result) => {
        this.result = result;
        this.phase = 'done';
        this.cdr.detectChanges();
      },
      error: () => {
        this.importError = 'An unexpected error occurred. Please try again.';
        this.phase = 'done';
        this.cdr.detectChanges();
      }
    });

    (event.target as HTMLInputElement).value = '';
  }

  closeAndRefresh(): void {
    this.dialogRef.close(this.result);
  }

  get hasErrors(): boolean {
    return (this.result?.errors?.length ?? 0) > 0;
  }

  get errorText(): string {
    return (this.result?.errors ?? [])
      .map((e) => `[${e.schoolName}] (row ${e.rowNumber}): ${e.errorMessage}`)
      .join('\n');
  }
}
